import { LoadingAnimation, Wizard } from '@odmonorepo/core-components';
import { Stack } from 'react-bootstrap';

import RequiredFirstDraw from './Cards/RequiredFirstDraw/RequiredFirstDraw';
import { CheckoutSuccess } from './Cards/CheckoutSuccess/CheckoutSuccess';
import OfferReview from './Cards/OfferReview/OfferReview';
import TermsAndAgreements from './Cards/TermsAndAgreements/TermsAndAgreements';
import Identity from './Cards/Identity/Identity';
import { BankVerification } from './Cards/BankData/BankVerification';
import IdentityChallenge from './Cards/Identity/IdentityChallenge';
import IdentityFailed from './Failures/IdentityFailed';
import UpdateBank from './Failures/UpdateBank';
import { PlaidLauncherCard } from './Cards/PlaidLauncher/PlaidLauncherCard';
import LDRequest from './Cards/LDR/LDRequest';
import { FINCEN } from './Cards/FINCEN/FINCEN';

import { useCheckoutStep } from './useCheckoutStep';
import { useDetailedOffers } from 'merchant-api/offer';
import { getLDR, useParties } from 'merchant-api/spiceroad';
import { useOptimizelyVariation, PLAID_ADOPTION_FLAG, FINCEN_PLACEMENT_FLAG } from 'merchant-api/vendor/optimizely';
import { useAnalytics } from 'use-analytics';
import { AppError } from 'merchant-api';

export const FINCEN_PLACEMENT_AFTER_OFFER_ACCEPTED = 'placement_after_offer_accepted';
export const FINCEN_PLACEMENT_AFTER_PLAID_CONNECTION = 'placement_after_plaid_connection';

export const checkoutSteps = {
  OFFER_REVIEW: <OfferReview />,
  FINCEN: <FINCEN />,
  BANK_VERIFICATION: <BankVerification />,
  PLAID_LAUNCHER: <PlaidLauncherCard />,
  IDENTITY: <Identity />,
  REQUIRED_FIRST_DRAW: <RequiredFirstDraw />,
  TERMS_AND_AGREEMENTS: <TermsAndAgreements />,
  CHECKOUT_SUCCESS: <CheckoutSuccess />,
  IDENTITY_CHALLENGE: <IdentityChallenge />,
  IDENTITY_FAILED: <IdentityFailed />,
  UPDATE_BANK: <UpdateBank />,
  LD_REQUEST: <LDRequest />
}

type StepKey = keyof typeof checkoutSteps | `$${keyof typeof checkoutSteps}`;

export const stepOrderLOC_plaid_flow: StepKey[]  = [
  'OFFER_REVIEW',
  'BANK_VERIFICATION',
  'IDENTITY',
  'REQUIRED_FIRST_DRAW',
  'TERMS_AND_AGREEMENTS',
  'PLAID_LAUNCHER',
  'CHECKOUT_SUCCESS',
] as const;

export const stepOrderTL_plaid_flow: StepKey[]  = [
  'OFFER_REVIEW',
  'BANK_VERIFICATION',
  'IDENTITY',
  'TERMS_AND_AGREEMENTS',
  'PLAID_LAUNCHER',
  'CHECKOUT_SUCCESS',
] as const;

export const stepOrderLOC_pre_agreement_flow: StepKey[] = [
  'OFFER_REVIEW',
  'BANK_VERIFICATION',
  'PLAID_LAUNCHER',
  'IDENTITY',
  'REQUIRED_FIRST_DRAW',
  'TERMS_AND_AGREEMENTS',
  'CHECKOUT_SUCCESS',
] as const;

export const stepOrderTL_pre_agreement_flow: StepKey[]  = [
  'OFFER_REVIEW',
  'BANK_VERIFICATION',
  'PLAID_LAUNCHER',
  'IDENTITY',
  'TERMS_AND_AGREEMENTS',
  'CHECKOUT_SUCCESS',
] as const;

const getStepOrderWithFincen = (baseOrder: StepKey[], fincenPlacement: string | undefined): StepKey[] => {
  const order = baseOrder.filter((step) => step !== 'FINCEN');

  if (fincenPlacement === FINCEN_PLACEMENT_AFTER_OFFER_ACCEPTED) {
    const offerIndex = order.indexOf('OFFER_REVIEW');
    if (offerIndex !== -1) {
      order.splice(offerIndex + 1, 0, 'FINCEN');
    }
    return order;
  }

  if (fincenPlacement === FINCEN_PLACEMENT_AFTER_PLAID_CONNECTION) {
    const termsIndex = order.indexOf('TERMS_AND_AGREEMENTS');
    if (termsIndex !== -1) {
      order.splice(termsIndex, 0, 'FINCEN');
    }
    return order;
  }

  return order;
};


export const CheckoutWizard = () => {

  const { step, stepLoading, stepError } = useCheckoutStep();

  const { selectedOffer, isLoadingDetailedOffers } = useDetailedOffers();

  const { isLoadingParties, isErrorParties, isMultiOwner } = useParties();

  const { data: plaidAdoptionVariation, isLoading: isLoadingPlaidAdoption } = useOptimizelyVariation({
    featureKey: PLAID_ADOPTION_FLAG,
    analytics: useAnalytics(),
  });

  const { data: fincenPlacementVariation, isLoading: isLoadingFincenPlacementVariation } = useOptimizelyVariation({
    featureKey: FINCEN_PLACEMENT_FLAG,
    analytics: useAnalytics(),
  });

  const { ldrError, ldrLoading, loanDocRequest } = getLDR();

  if(stepError) throw new Error('Error loading checkout step');
  if(isErrorParties) throw new Error('Error loading parties data');
  if (isMultiOwner) {
    throw AppError.MultiOwner;
  }
  if (ldrError) throw ldrError;

  if (stepLoading || isLoadingParties || isLoadingPlaidAdoption || isLoadingDetailedOffers || isLoadingFincenPlacementVariation || ldrLoading) {
    return (
      <Stack>
        <LoadingAnimation className="mx-auto mt-7" />
      </Stack>
    );
  }

  const isPlaidFlow = plaidAdoptionVariation === 'plaid_flow';
  let baseStepOrder: StepKey[];
  if (selectedOffer?.product === 'TermLoan') {
    baseStepOrder = isPlaidFlow ? stepOrderTL_plaid_flow : stepOrderTL_pre_agreement_flow;
  } else {
    baseStepOrder = isPlaidFlow ? stepOrderLOC_plaid_flow : stepOrderLOC_pre_agreement_flow;
  }

  const stepOrder = getStepOrderWithFincen(baseStepOrder, fincenPlacementVariation);

  const hasClosingLoanDocRequest = loanDocRequest?.some(
    (request) => request.classification === 'CLOSING' && (request.status === 'REQUESTED' || request.status === 'MERCHANT_RESPONSE_SUBMITTED' || request.status === 'REJECTED'),
  );

  const initialStep = hasClosingLoanDocRequest ? 'LD_REQUEST' : step || 'OFFER_REVIEW';

  return (
    <Wizard
      steps={checkoutSteps}
      stepOrder={stepOrder}
      initialStep={initialStep}
      onStepChange={() =>
        window.scrollTo({
          top: 0,
          behavior: 'smooth',
        })
      }
    />
  );
};

export default CheckoutWizard;
